import { useEffect } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePsfSharePageData, usePsfSharePageFcm } from 'src/app/pages/psf/share/pageContext';
import type { ShareFeedItem } from 'src/app/pages/psf/share/pageContext';
import { usePageCopy } from 'src/app/pages/psf/share/shared/hooks';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

type Result = ModelHookResult<{
	heading: string;
	subHeading: string;
	warning?: string;
	shareFeed: {
		items: readonly ShareFeedItem[];
		showFeed: boolean;
	};
	isPsfOnlyShare: boolean;
	pageTitle: string;
	hideStepperProgress: boolean;
}>;

const MIN_SHARES = 3;

export function usePageBody(): Result {
	const {
		data: { headingText, subheadingText, progressBarLabelText },
	} = usePageCopy();
	const { warningMessage, psfOnlyShareButtonList } = usePsfSharePageFcm();
	const {
		data: { shareFeed },
	} = usePsfSharePageData();
	const trackOnce = useTrackOnce();

	const showFeed = shareFeed.length >= MIN_SHARES;

	const { translate } = useI18n();
	const sourceLocation = useQueryParamValue('source_location');

	const hideStepperProgress = ['petition_show', 'petition_update', 'promotion_completion_email'].includes(
		sourceLocation || '',
	);
	const pageTitle = hideStepperProgress ? translate('fe.components.psf.signature_goal_label') : progressBarLabelText;

	useEffect(() => {
		if (!showFeed) return;
		void trackOnce('share_feed_view', {
			share_feed_item_count: shareFeed.length,
			share_feed_photo_count: shareFeed.filter((item) => !!item.userPhoto).length,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trackOnce]);

	return {
		data: {
			heading: headingText,
			subHeading: subheadingText,
			warning: warningMessage,
			shareFeed: {
				items: shareFeed,
				showFeed,
			},
			isPsfOnlyShare: psfOnlyShareButtonList.length > 0,
			pageTitle,
			hideStepperProgress,
		},
	};
}
